import type { CreateClientConfig } from '@/api-client/client.gen'
import type { ClientOptions, Config } from '@/api-client/client'
import * as Sentry from '@sentry/vue'
import { userManager, useAuth } from '@/composables/useAuth'
import { sentryConfig } from './sentry'
import { oidcConfig } from './oidc'
import axios from 'axios'
import { currentViewSignal } from '@/utils/viewRequests'

const { handleAuthFailure } = useAuth()

const axiosInstance = axios.create()

// GET calls belong to the open record. Abort them when that record changes.
// POST/PUT/PATCH/DELETE are user actions and are left to finish.
axiosInstance.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase()
  if (!config.signal && method === 'get') {
    config.signal = currentViewSignal()
  }
  return config
})

// Request interceptor - add breadcrumbs
axiosInstance.interceptors.request.use(
  (config) => {
    if (sentryConfig.enabled) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        message: `${config.method?.toUpperCase()} ${config.url}`,
        data: {
          url: config.url,
          method: config.method,
        },
      })
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor - handle errors and add breadcrumbs
axiosInstance.interceptors.response.use(
  (response) => {
    if (sentryConfig.enabled) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
        data: {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
        },
      })
    }
    return response
  },
  async (error) => {
    if (sentryConfig.enabled) {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'error',
        message: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network Error'}`,
        data: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
        },
      })
    }

    if (error?.response?.status === 401) {
      await handleAuthFailure()
    } else if (error?.response?.status && sentryConfig.enabled) {
      // Log non-401 errors to Sentry (401 is handled by auth)
      Sentry.captureException(error, {
        tags: {
          errorType: 'api',
          httpStatus: error.response.status,
        },
      })
      console.error('API error:', error)
    }

    return Promise.reject(error)
  },
)

const auth = async () => {
  try {
    const user = await userManager.getUser()
    const token = user?.access_token
    //console.log('token', token)
    return token ? token : undefined
  } catch (error) {
    console.error('Error getting auth token:', error)
    return undefined
  }
}

// API configuration
export const apiConfig = {
  baseURL: import.meta.env.BASE_URL || '/',
}

export const createClientConfig: CreateClientConfig = (
  config,
): Config<Required<ClientOptions>> => ({
  ...config,
  auth: auth,
  axios: axiosInstance,
  baseURL: oidcConfig.authority,
})
